import { createSlice } from "@reduxjs/toolkit";

import { ALL } from "../../consts/driverDetails";

import {
  mergeDriverDayLocked,
  mergeDriverDayUnlocked,
  mergeRouteDayLocked,
  mergeRouteDayUnlocked,
} from "../../utils/matrixDays";

import {
  resetDriverManagement,
  setDriverView,
  fetchDepotMatrixWeek,
  refreshDepotMatrixWeekLocks,
  refreshDepotMatrixDayLocks,
  lockDriverProfile,
  unlockDriverProfile,
  setUserSessionExpired,
  setLocksRefreshRequired,
  lockDriverDay,
  unlockDriverDay,
  lockRouteDay,
  unlockRouteDay,
  editDriver,
  updateDriverDayEdit,
  updateDriverProfileEdit,
  saveMatrixWeek,
  fetchDepotSectionWeek,
  fetchVehicles,
  fetchCountries,
  fetchDriverPrefixes,
  setIsDriverEditProfileValid,
  setIsDriverEditWorkScheduleValid,
  resetDriver,
  unlockDepotWeek,
  forceUnlockMatrixWeek,
  forceUnlockDriverWeek,
  fetchDriverImage,
  saveDriverProfile,
  addDriver,
  removeDriver,
  updateDriverImage,
  setSelectingDriverLocation,
  setNewDriverEdit,
  setNewDriverUsers,
  setNewDriverProfileEdit,
  setHideDriverManagementModal,
  copyDriverDayEdit,
} from "./thunkActions";

const getEmptyMatrixDay = () => ({
  drivers: {},
  routes: [],
  driverDayLocks: {},
  routeDayLocks: {},
});

const getEmptyMatrixDays = () =>
  [0, 1, 2, 3, 4, 5, 6].reduce((acc, dayOfWeek) => {
    acc[dayOfWeek] = getEmptyMatrixDay();
    return acc;
  }, {});

const initialState = {
  loading: false,
  userSessionExpired: false,
  locksRefreshRequired: {
    0: false,
    1: false,
    2: false,
    3: false,
    4: false,
    5: false,
    6: false,
    profile: false,
  },
  hideDriverManagementModal: false,
  view: ALL,
  sessionChecksum: null,
  driverProfiles: {},
  driverProfileLocks: {},
  matrixDays: getEmptyMatrixDays(),
  sectionDays: {},
  vehicles: {},
  countries: [],
  driverPrefixes: null,
  isDriverEditProfileValid: null,
  isDriverEditWorkScheduleValid: null,
  driversImpacted: {},
  driverImages: {},
  newDriverEdit: null,
  driverEdit: null,
  driverEditCopy: null,
  selectingDriverLocation: null,
};

export const driverManagementSlice = createSlice({
  name: "driverManagement",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(resetDriverManagement.fulfilled, () => initialState)
      .addCase(setDriverView, (state, action) => {
        state.view = action.payload;
      })
      .addCase(setHideDriverManagementModal, (state, action) => {
        state.hideDriverManagementModal = action.payload;
      })
      .addCase(setSelectingDriverLocation, (state, action) => {
        state.selectingDriverLocation = action.payload;
      })
      .addCase(setNewDriverEdit, (state, action) => {
        state.newDriverEdit = action.payload;
      })
      .addCase(setNewDriverUsers, (state, action) => {
        state.newDriverEdit = {
          ...state.newDriverEdit,
          users: action.payload,
        };
      })
      .addCase(setNewDriverProfileEdit, (state, action) => {
        state.newDriverEdit = {
          ...state.newDriverEdit,
          driverProfileEdit: action.payload,
        };
      })
      .addCase(fetchVehicles.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVehicles.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.vehicles = action.payload;
      })
      .addCase(fetchCountries.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCountries.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchCountries.fulfilled, (state, action) => {
        state.countries = action.payload;
      })
      .addCase(fetchDriverPrefixes.pending, (state, action) => {
        state.driverPrefixes = null;
      })
      .addCase(fetchDriverPrefixes.rejected, (state, action) => {
        state.driverPrefixes = null;
      })
      .addCase(fetchDriverPrefixes.fulfilled, (state, action) => {
        state.driverPrefixes = action.payload;
      })
      .addCase(fetchDepotSectionWeek.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDepotSectionWeek.rejected, (state) => {
        state.loading = false;
      })
      .addCase(fetchDepotSectionWeek.fulfilled, (state, action) => {
        state.sectionDays = action.payload;
      })
      .addCase(fetchDepotMatrixWeek.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchDepotMatrixWeek.fulfilled, (state, action) => {
        state.loading = false;
        const {
          sessionChecksum,
          driverProfiles,
          driverProfileLocks,
          matrixDays,
        } = action.payload;
        state.sessionChecksum = sessionChecksum;
        state.driverImages = {};
        state.driverProfiles = driverProfiles;
        state.driverProfileLocks = driverProfileLocks;
        state.matrixDays = matrixDays;
      })
      .addCase(forceUnlockMatrixWeek.pending, (state) => {
        state.loading = true;
        state.selectedDriver = null;
        state.isDriverEditProfileValid = true;
        state.isDriverEditWorkScheduleValid = true;
        state.driversImpacted = {};
        state.driverEdit = null;
        state.driverEditCopy = null;
        state.driverImageCopy = null;
      })
      .addCase(forceUnlockMatrixWeek.rejected, (state) => {
        state.loading = false;
      })
      .addCase(forceUnlockDriverWeek.pending, (state) => {
        state.loading = true;
        state.selectedDriver = null;
        state.isDriverEditProfileValid = true;
        state.isDriverEditWorkScheduleValid = true;
        state.driversImpacted = {};
        state.driverEdit = null;
        state.driverEditCopy = null;
        state.driverImageCopy = null;
      })
      .addCase(forceUnlockDriverWeek.rejected, (state) => {
        state.loading = false;
      })
      .addCase(refreshDepotMatrixWeekLocks.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshDepotMatrixWeekLocks.fulfilled, (state, action) => {
        const { driverProfileLocks, matrixDays } = action.payload;
        state.locksRefreshRequired = {
          0: false,
          1: false,
          2: false,
          3: false,
          4: false,
          5: false,
          6: false,
          profile: false,
        };
        state.driverProfileLocks = driverProfileLocks;
        state.matrixDays = matrixDays;
      })
      .addCase(refreshDepotMatrixDayLocks.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshDepotMatrixDayLocks.fulfilled, (state, action) => {
        const { dayOfWeek, matrixDays } = action.payload;
        state.locksRefreshRequired = {
          ...state.locksRefreshRequired,
          [dayOfWeek]: false,
        };
        state.matrixDays = matrixDays;
      })
      .addCase(copyDriverDayEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(copyDriverDayEdit.rejected, (state) => {
        state.loading = false;
      })
      .addCase(copyDriverDayEdit.fulfilled, (state, action) => {
        const { driverEdit, driversImpacted } = action.payload;
        state.loading = false;
        state.driverEdit = driverEdit;
        state.driversImpacted = driversImpacted;
      })
      .addCase(lockDriverProfile.fulfilled, (state, action) => {
        const {
          driverCode,
          lockedByUserCode,
          lockedByUserName,
          lockedDatetime,
        } = action.payload;
        state.driverProfileLocks = {
          ...state.driverProfileLocks,
          [driverCode]: {
            checksum: state.driverProfileLocks[driverCode].checksum,
            lockedByUserCode,
            lockedByUserName,
            lockedDatetime,
            lockedByYou: true,
            lockedInDifferentBrowser: false,
          },
        };
        state.matrixDays = [0, 1, 2, 3, 4, 5, 6].reduce(
          (acc, dayOfWeek) =>
            mergeDriverDayLocked(
              { matrixDays: acc },
              {
                dayOfWeek,
                driverCode,
                lockedByUserCode,
                lockedByUserName,
                lockedDatetime,
              }
            ),
          state.matrixDays
        );
      })
      .addCase(unlockDriverProfile.fulfilled, (state, action) => {
        const { driverCode } = action.payload;
        state.driverProfileLocks = {
          ...state.driverProfileLocks,
          [driverCode]: {
            checksum: state.driverProfileLocks[driverCode].checksum,
            lockedByUserCode: null,
            lockedByUserName: null,
            lockedDatetime: null,
            lockedByYou: false,
            lockedInDifferentBrowser: false,
          },
        };
        state.matrixDays = [0, 1, 2, 3, 4, 5, 6].reduce(
          (acc, dayOfWeek) =>
            mergeDriverDayUnlocked(
              { matrixDays: acc },
              {
                dayOfWeek,
                driverCode,
              }
            ),
          state.matrixDays
        );
      })
      .addCase(setUserSessionExpired, (state, action) => {
        state.userSessionExpired = action.payload;
      })
      .addCase(setLocksRefreshRequired, (state, action) => {
        state.locksRefreshRequired = {
          [action.payload]: true,
        };
      })
      .addCase(lockDriverDay.fulfilled, (state, action) => {
        state.matrixDays = mergeDriverDayLocked(state, action.payload);
      })
      .addCase(unlockDriverDay.fulfilled, (state, action) => {
        state.matrixDays = mergeDriverDayUnlocked(state, action.payload);
      })
      .addCase(lockRouteDay.fulfilled, (state, action) => {
        state.matrixDays = mergeRouteDayLocked(state, action.payload);
      })
      .addCase(unlockRouteDay.fulfilled, (state, action) => {
        state.matrixDays = mergeRouteDayUnlocked(state, action.payload);
      })
      .addCase(fetchDriverImage.pending, (state, action) => {
        const { driverCode } = action.meta.arg;
        state.driverImages = {
          ...state.driverImages,
          [driverCode]: "pending",
        };
      })
      .addCase(fetchDriverImage.rejected, (state, action) => {
        const { driverCode } = action.meta.arg;
        state.driverImages = {
          ...state.driverImages,
          [driverCode]: "rejected",
        };
      })
      .addCase(fetchDriverImage.fulfilled, (state, action) => {
        const { driverCode, image } = action.payload;
        state.driverImages = {
          ...state.driverImages,
          [driverCode]: image,
        };
        state.driverImageCopy = image;
      })
      .addCase(updateDriverImage.pending, (state) => {
        state.loading = true;
        state.driverImages = {
          ...state.driverImages,
          [state.driverEdit.driverCode]: "pending",
        };
      })
      .addCase(updateDriverImage.rejected, (state) => {
        state.loading = false;
        state.driverImages = {
          ...state.driverImages,
          [state.driverEdit.driverCode]: "rejected",
        };
      })
      .addCase(updateDriverImage.fulfilled, (state, action) => {
        const { image } = action.payload;
        state.loading = false;
        state.driverEdit = {
          ...state.driverEdit,
          imageChanged: true,
        };
        state.driverImages = {
          ...state.driverImages,
          [state.driverEdit.driverCode]: image,
        };
      })
      .addCase(editDriver.fulfilled, (state, action) => {
        state.selectedDriver = null;
        state.isDriverEditProfileValid = true;
        state.isDriverEditWorkScheduleValid = true;
        state.driverEdit = action.payload.driverEdit;
        state.driverEditCopy = action.payload.driverEdit;
        state.driverImageCopy =
          state.driverImages[action.payload.driverEdit.driverCode];
      })
      .addCase(resetDriver, (state) => {
        state.driversImpacted = {};
        state.driverImages = {
          ...state.driverImages,
          [state.driverEdit.driverCode]: state.driverImageCopy,
        };
        state.driverEdit = null;
        state.driverEditCopy = null;
        state.driverImageCopy = null;
      })
      .addCase(updateDriverDayEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDriverDayEdit.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateDriverDayEdit.fulfilled, (state, action) => {
        const { driverEdit, driversImpacted, keepLoading } = action.payload;
        state.loading = keepLoading;
        state.driverEdit = driverEdit;
        state.driversImpacted = driversImpacted;
      })
      .addCase(updateDriverProfileEdit.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateDriverProfileEdit.rejected, (state) => {
        state.loading = false;
      })
      .addCase(updateDriverProfileEdit.fulfilled, (state, action) => {
        const { newDriverProfileEdit, newDriverDayEdits, keepLoading } =
          action.payload;
        const newDriverEdit = {
          ...state.driverEdit,
          driverProfileEdit: newDriverProfileEdit,
          driverDayEdits: newDriverDayEdits,
        };
        state.loading = keepLoading;
        state.driverEdit = { ...newDriverEdit };
      })
      .addCase(setIsDriverEditProfileValid, (state, action) => {
        state.isDriverEditProfileValid = action.payload;
      })
      .addCase(setIsDriverEditWorkScheduleValid, (state, action) => {
        state.isDriverEditWorkScheduleValid = action.payload;
      })
      .addCase(unlockDepotWeek.pending, (state) => {
        state.loading = true;
      })
      .addCase(unlockDepotWeek.rejected, (state) => {
        state.loading = false;
      })
      .addCase(unlockDepotWeek.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(saveMatrixWeek.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveMatrixWeek.rejected, (state) => {
        state.loading = false;
      })
      .addCase(saveMatrixWeek.fulfilled, (state, action) => {
        const { newMatrixDays } = action.payload;
        state.matrixDays = newMatrixDays;
        state.loading = false;
        state.driversImpacted = {};
        state.driverEdit = null;
        state.driverEditCopy = null;
        state.driverImageCopy = null;
      })
      .addCase(addDriver.pending, (state) => {
        state.loading = true;
      })
      .addCase(addDriver.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addDriver.fulfilled, (state, action) => {
        const { driverProfiles, driverProfileLocks, matrixDays } =
          action.payload;

        state.loading = false;
        state.driverProfiles = driverProfiles;
        state.driverProfileLocks = driverProfileLocks;
        state.matrixDays = matrixDays;
      })
      .addCase(removeDriver.pending, (state) => {
        state.loading = true;
        state.hideDriverManagementModal = true;
      })
      .addCase(removeDriver.rejected, (state) => {
        state.loading = false;
        state.hideDriverManagementModal = false;
      })
      .addCase(removeDriver.fulfilled, (state, action) => {
        const { driverProfiles, driverProfileLocks, matrixDays } =
          action.payload;
        state.loading = false;
        state.hideDriverManagementModal = false;
        state.driverProfiles = driverProfiles;
        state.driverProfileLocks = driverProfileLocks;
        state.matrixDays = matrixDays;
      })
      .addCase(saveDriverProfile.pending, (state) => {
        state.loading = true;
      })
      .addCase(saveDriverProfile.rejected, (state) => {
        state.loading = false;
      })
      .addCase(saveDriverProfile.fulfilled, (state, action) => {
        const { newDriverProfile, newDriverProfileLocks, newMatrixDays } =
          action.payload;
        state.driverProfiles = {
          ...state.driverProfiles,
          [newDriverProfile.driverCode]: newDriverProfile,
        };
        state.driverProfileLocks = newDriverProfileLocks;
        state.matrixDays = newMatrixDays;
        state.loading = false;
        state.driversImpacted = {};
        state.driverEdit = null;
        state.driverEditCopy = null;
        state.driverImageCopy = null;
      });
  },
});

const { reducer } = driverManagementSlice;
export { initialState, reducer };
